/**
 * CalibreBulkActionBar — floating bulk-action bar for a calibre selection.
 *
 * The same shape as the References listing's ReferenceBulkActionBar (dark pill
 * pinned to the bottom of the viewport, count → divider → Clear → actions), so
 * both listings behave identically once rows are ticked.
 *
 * Unlike the References bar this one is PRESENTATIONAL: every handler, modal and
 * write stays in CuratedCalibres, which also owns the selection and its
 * hydration. Only the buttons moved here.
 */

export interface CalibreBulkActionBarProps {
  /** Number of selected calibres. The caller renders the bar only when > 0. */
  count: number;
  /**
   * A bulk write is in flight. Disables every action so a second one can't be
   * started on top of it.
   */
  busy?: boolean;
  /**
   * The selection is being read back as full Firestore documents (the gate the
   * AI and image-source tools run before they open). Disables the actions and
   * shows a spinner beside the count.
   */
  hydrating?: boolean;
  /**
   * Every selected calibre has a NON-GENERIC manufacturer, so a brand can be
   * derived for each of them. Gates the two brand-filling actions: they are
   * all-or-nothing over the selection rather than "apply to the ones that
   * qualify", so a single generic or unclassified row disables both.
   */
  brandActionsEnabled?: boolean;
  /** Why the brand actions are off, as their tooltip. */
  brandActionsDisabledReason?: string;
  onAiEnhance: () => void;
  onSetManufacturer: () => void;
  onSetBrand: () => void;
  onSetAkaBrands: () => void;
  onSetImageSource: () => void;
  onSetCalibreSource: () => void;
  /** Open the AI tier backfill scoped to the selection. */
  onBackfillTiers: () => void;
  onClearSelection: () => void;
}

export function CalibreBulkActionBar({
  count,
  busy = false,
  hydrating = false,
  brandActionsEnabled = false,
  brandActionsDisabledReason,
  onAiEnhance,
  onSetManufacturer,
  onSetBrand,
  onSetAkaBrands,
  onSetImageSource,
  onSetCalibreSource,
  onBackfillTiers,
  onClearSelection,
}: CalibreBulkActionBarProps) {
  const disabled = busy || hydrating || count === 0;
  const brandDisabled = disabled || !brandActionsEnabled;

  return (
    <div className='fixed bottom-6 left-1/2 -translate-x-1/2 z-50'>
      <div className='flex items-center gap-3 bg-gray-900 text-white px-5 py-3 rounded-2xl shadow-2xl'>
        <span className='text-sm font-medium whitespace-nowrap'>
          {count.toLocaleString()} selected
          {hydrating && <span className='text-gray-400 ml-1'>(loading...)</span>}
        </span>
        <div className='h-4 w-px bg-gray-600' />
        <button
          onClick={onClearSelection}
          disabled={busy}
          className='text-gray-400 hover:text-white disabled:opacity-50 text-sm transition-colors'
        >
          Clear
        </button>
        <button
          onClick={onAiEnhance}
          disabled={disabled}
          title='Run AI enrichment over the selected calibres, then review and apply per-field'
          className='bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-sm px-3 py-1.5 rounded-lg whitespace-nowrap transition-colors flex items-center gap-2'
        >
          {hydrating && (
            <div className='animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white' />
          )}
          AI Enhance
        </button>
        <button
          onClick={onSetManufacturer}
          disabled={disabled}
          className='bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-sm px-3 py-1.5 rounded-lg whitespace-nowrap transition-colors'
        >
          Set Manufacturer
        </button>
        <button
          onClick={onSetBrand}
          disabled={brandDisabled}
          title={
            brandActionsEnabled
              ? "Set each calibre's brand to its manufacturer's default brand (never overwrites a brand already set)"
              : brandActionsDisabledReason
          }
          className='bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-sm px-3 py-1.5 rounded-lg whitespace-nowrap transition-colors'
        >
          Set Brand
        </button>
        <button
          onClick={onSetAkaBrands}
          disabled={brandDisabled}
          title={
            brandActionsEnabled
              ? "Scope every unbranded AKA to its manufacturer's default brand (already-scoped AKAs are left alone)"
              : brandActionsDisabledReason
          }
          className='bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-sm px-3 py-1.5 rounded-lg whitespace-nowrap transition-colors'
        >
          Set AKA Brands
        </button>
        <button
          onClick={onSetImageSource}
          disabled={disabled}
          className='bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-sm px-3 py-1.5 rounded-lg whitespace-nowrap transition-colors'
        >
          Set image source
        </button>
        <button
          onClick={onSetCalibreSource}
          disabled={disabled}
          className='bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-sm px-3 py-1.5 rounded-lg whitespace-nowrap transition-colors'
        >
          Set calibre source
        </button>
        <button
          onClick={onBackfillTiers}
          disabled={disabled}
          title='Re-run AI enrichment on the selected calibres and auto-accept any movement tiers it returns'
          className='bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-sm px-3 py-1.5 rounded-lg whitespace-nowrap transition-colors'
        >
          Backfill AI tiers
        </button>
      </div>
    </div>
  );
}
